import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';

const STATUS_LABELS = {
  email_pending:    'Email Pending',
  verified:         'Verified',
  report_generated: 'Report Ready',
};

const AWS_REGIONS = [
  'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
  'ap-northeast-1', 'ap-northeast-2', 'ap-southeast-1', 'ap-southeast-2',
  'ca-central-1', 'eu-central-1', 'eu-west-1', 'eu-west-2', 'sa-east-1',
];

const Customers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    awsAccountId: '',
    workspacesRegion: 'us-east-1',
    contactEmail: '',
    notes: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [editingRegion, setEditingRegion] = useState('');
  const [editingAccountId, setEditingAccountId] = useState('');
  const [cfOpenId, setCfOpenId] = useState(null);
  const [cfTemplates, setCfTemplates] = useState({});
  const [cfLoading, setCfLoading] = useState({});

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (location.state?.openCreate) setShowForm(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/customers');
      setCustomers(data.customers || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      alert('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    if (!formData.customerName || !formData.awsAccountId) {
      alert('Please fill in all required fields');
      return;
    }
    try {
      setLoading(true);
      await api.post('/customers', formData);
      setFormData({ customerName: '', awsAccountId: '', workspacesRegion: 'us-east-1', contactEmail: '', notes: '' });
      setShowForm(false);
      loadCustomers();
    } catch (error) {
      console.error('Error creating customer:', error);
      alert('Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStart = (customer) => {
    setEditingId(customer.customerId);
    setEditingRegion(customer.workspacesRegion || 'us-east-1');
    setEditingAccountId(customer.awsAccountId || '');
  };

  const handleEditSave = async (customer) => {
    try {
      setLoading(true);
      await api.put(`/customers/${customer.customerId}`, {
        workspacesRegion: editingRegion,
        awsAccountId: editingAccountId,
      });
      setEditingId(null);
      loadCustomers();
    } catch (error) {
      console.error('Error updating customer:', error);
      alert(error.response?.data?.error || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCf = async (customer) => {
    if (cfOpenId === customer.customerId) {
      setCfOpenId(null);
      return;
    }
    setCfOpenId(customer.customerId);
  };

  const handleGenerateCfTemplate = async (customer) => {
    setCfLoading(prev => ({ ...prev, [customer.customerId]: true }));
    try {
      const { data } = await api.post('/cloudformation/generate', { customerId: customer.customerId });
      setCfTemplates(prev => ({ ...prev, [customer.customerId]: JSON.stringify(data.template, null, 2) }));
    } catch (error) {
      console.error('Error generating template:', error);
      alert('Failed to generate CloudFormation template');
    } finally {
      setCfLoading(prev => ({ ...prev, [customer.customerId]: false }));
    }
  };

  const handleCfDownload = (customer) => {
    const template = cfTemplates[customer.customerId];
    const a = document.createElement('a');
    a.href = 'data:text/plain;charset=utf-8,' + encodeURIComponent(template);
    a.download = `clickimpact-role-${customer.customerId}.json`;
    a.click();
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Delete ${customer.customerName}? This cannot be undone.`)) return;
    try {
      setLoading(true);
      await api.delete(`/customers/${customer.customerId}`);
      loadCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert(error.response?.data?.error || 'Failed to delete customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="container">
      <div className="card">
        <h2>Manage Customers</h2>
        <button onClick={() => setShowForm(!showForm)} style={{ marginBottom: '1rem' }}>
          {showForm ? 'Cancel' : '+ New Customer'}
        </button>

        {showForm && (
          <form onSubmit={handleCreateCustomer} style={{ marginBottom: '2rem' }}>
            <div className="form-group">
              <label htmlFor="customerName">Customer Name *</label>
              <input
                id="customerName"
                name="customerName"
                type="text"
                value={formData.customerName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="awsAccountId">AWS Account ID *</label>
              <input
                id="awsAccountId"
                name="awsAccountId"
                type="text"
                placeholder="123456789012"
                value={formData.awsAccountId}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="workspacesRegion">WorkSpaces Region *</label>
              <select
                id="workspacesRegion"
                name="workspacesRegion"
                value={formData.workspacesRegion}
                onChange={handleInputChange}
                required
              >
                {AWS_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="contactEmail">Contact Email</label>
              <input
                id="contactEmail"
                name="contactEmail"
                type="email"
                value={formData.contactEmail}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
            <button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Customer'}
            </button>
          </form>
        )}

        {loading && <div className="spinner"></div>}

        {customers.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Customer Name</th>
                <th>AWS Account ID</th>
                <th>Region</th>
                <th>Contact Email</th>
                <th>Type</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => {
                const isEditing = editingId === customer.customerId;
                const isSelfService = customer.type === 'self_service';
                const isEstimate = customer.type === 'pricing_estimate';
                const isReadOnly = isSelfService || isEstimate;
                const isCfOpen = cfOpenId === customer.customerId;
                const cfTemplate = cfTemplates[customer.customerId];
                const isCfLoading = cfLoading[customer.customerId];
                return (
                  <React.Fragment key={customer.customerId}>
                    <tr>
                      <td>{customer.customerName}</td>
                      <td>
                        {isEditing && !isReadOnly
                          ? <input
                              type="text"
                              value={editingAccountId}
                              onChange={(e) => setEditingAccountId(e.target.value)}
                              style={{ width: '130px', fontSize: 13 }}
                            />
                          : isEstimate
                            ? <span style={{ color: 'var(--color-text-secondary)' }}>—</span>
                            : (customer.awsAccountId || '-')}
                      </td>
                      <td>
                        {isEditing && !isReadOnly
                          ? <select value={editingRegion} onChange={(e) => setEditingRegion(e.target.value)} style={{ fontSize: 13 }}>
                              {AWS_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                          : (customer.workspacesRegion || 'us-east-1')}
                      </td>
                      <td>{customer.contactEmail || (isSelfService ? customer.customerName : '-')}</td>
                      <td>
                        {isSelfService && (
                          <span style={{ fontSize: 11, fontWeight: 600, background: 'rgba(62, 0, 255, 0.08)', color: '#1801B4', borderRadius: 3, padding: '2px 6px' }}>Self-Service</span>
                        )}
                        {isEstimate && (
                          <span style={{ fontSize: 11, fontWeight: 600, background: 'rgba(91, 73, 222, 0.08)', color: '#5B49DE', borderRadius: 3, padding: '2px 6px' }}>Estimate</span>
                        )}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                        {isSelfService ? STATUS_LABELS[customer.status] || customer.status : ''}
                      </td>
                      <td>{new Date(customer.createdAt).toLocaleDateString()}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {isEditing ? (
                          <>
                            <button onClick={() => handleEditSave(customer)} disabled={loading} className="btn-success btn-sm" style={{ marginRight: 6 }}>Save</button>
                            <button onClick={() => setEditingId(null)} className="btn-secondary btn-sm">Cancel</button>
                          </>
                        ) : (
                          <>
                            {!isReadOnly && <button onClick={() => handleEditStart(customer)} className="btn-sm" style={{ marginRight: 6 }}>Edit</button>}
                            {!isReadOnly && (
                              <button onClick={() => handleToggleCf(customer)} className="btn-secondary btn-sm" style={{ marginRight: 6 }}>
                                IAM Role {isCfOpen ? '▲' : '▼'}
                              </button>
                            )}
                            {isEstimate && (
                              <button
                                onClick={() => navigate(`/calculator?estimateId=${customer.customerId}`)}
                                className="btn-sm"
                                style={{ marginRight: 6 }}
                              >
                                Open
                              </button>
                            )}
                            <button onClick={() => handleDelete(customer)} className="btn-danger-outline btn-sm">Delete</button>
                          </>
                        )}
                      </td>
                    </tr>
                    {isCfOpen && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0.75rem 1rem 1.25rem', background: 'var(--color-bg-subtle, #f8f9fa)', borderTop: 'none' }}>
                          <div style={{ maxWidth: 720 }}>
                            <strong style={{ fontSize: 13 }}>CloudFormation IAM Role</strong>
                            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0.375rem 0 0.75rem' }}>
                              Deploy this template in the customer's AWS account to create the read-only IAM role used for data collection.
                            </p>
                            {!cfTemplate && (
                              <button onClick={() => handleGenerateCfTemplate(customer)} disabled={isCfLoading} className="btn-sm">
                                {isCfLoading ? 'Generating…' : 'Generate Template'}
                              </button>
                            )}
                            {cfTemplate && (
                              <>
                                <pre className="code-block" style={{ maxHeight: 240, overflowY: 'auto', fontSize: 12 }}>
                                  {cfTemplate}
                                </pre>
                                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                                  <button onClick={() => handleCfDownload(customer)} className="btn-sm">Download</button>
                                  <button onClick={() => { navigator.clipboard.writeText(cfTemplate); alert('Copied!'); }} className="btn-secondary btn-sm">Copy</button>
                                </div>
                              </>
                            )}
                            <div style={{ marginTop: '1rem', fontSize: 13 }}>
                              <strong>Deployment steps:</strong>
                              <ol style={{ paddingLeft: '1.25rem', marginTop: '0.375rem' }}>
                                <li>Download the CloudFormation template above</li>
                                <li>In the customer's AWS account, go to CloudFormation → Create stack</li>
                                <li>Upload the template and create the stack</li>
                                <li>Once complete, go to Reports and click "Run Pre-Flight"</li>
                              </ol>
                              <p style={{ marginTop: '0.5rem' }}>
                                <strong>Can't deploy CloudFormation?</strong> Have the customer run <code>Get-WorkSpacesData.ps1</code> instead and upload the resulting JSON on the Reports page.
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && customers.length === 0 && (
          <p style={{ color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>No customers found. Create one to get started.</p>
        )}
      </div>
    </main>
  );
};

export default Customers;
